import { useEffect, useState } from "react";
import { Send, FileText, Loader2, Briefcase, Eye, Trash2, CheckCircle2, AlertCircle, UserX, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const BTW_LOGO = "https://customer-assets.emergentagent.com/wingman/9c8ef01f-de5d-404e-b131-b692bd44c30d/attachments/db9a87c5ef6f4b71a9e8327f6aad76a9_btw_logo_full.png";
const F3_LOGO = "https://customer-assets.emergentagent.com/wingman/9c8ef01f-de5d-404e-b131-b692bd44c30d/attachments/a91ef41ceb06452888e1e4d47f01588a_f3_logo_slanted.png";

const FRIENDLY_MANAGER = {
  "Maddie Wilson": "Ms. Maddie",
  "Power Armstrong": "Mr. Power",
};
const friendlyManager = (name) => FRIENDLY_MANAGER[name] || name || "";

const MANAGERS = ["Shift Manager", "Maddie Wilson", "Power Armstrong"];

const JOB_TITLES = [
  "Front of House Associate",
  "Assistant Manager Trainee",
  "Runner / Expediter",
  "Prep Utility",
  "Fry Cook",
  "Grill Cook",
  "Maintenance / Utility",
];

const HYGIENE_DOC_URL = "https://customer-assets.emergentagent.com/job_fast-catering-1/artifacts/ouom1iue_88D37B20-E9EB-4A5B-B2FD-77048A0C7265.png";

const MODE_OFFER = "offer";
const MODE_DECLINATION = "declination";

const emptyOfferForm = () => ({
  candidate_name: "",
  candidate_email: "",
  job_title: "",
  pay_rate: "",
  start_date: "",
  start_time: "",
  reporting_manager: "",
  responsibilities: "",
});

const emptyDeclForm = () => ({
  candidate_name: "",
  candidate_email: "",
  position: "",
  interview_date: "",
});

const fmtMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}/hr` : "—";
};

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return s;
  }
};

const fmtTime = (s) => {
  if (!s) return "";
  // Accept "HH:MM" 24h → render as 12h with AM/PM
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s).trim());
  if (!m) return String(s);
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
};

// Allowed start-time slots — 9:00 AM through 9:00 PM in 30-minute steps.
const START_TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 9; h <= 21; h += 1) {
    for (const m of [0, 30]) {
      if (h === 21 && m === 30) break; // stop at 9:00 PM sharp
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
})();

const fmtSent = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export const HiringTab = () => {
  const [mode, setMode] = useState(MODE_OFFER);
  const [offerForm, setOfferForm] = useState(emptyOfferForm());
  const [declForm, setDeclForm] = useState(emptyDeclForm());
  const [offers, setOffers] = useState([]);
  const [declinations, setDeclinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // History letter viewer modal — `kind` is "offer" or "declination"
  const [viewing, setViewing] = useState(null);

  const updateOffer = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setOfferForm((s) => ({ ...s, [k]: v }));
  };
  const updateDecl = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setDeclForm((s) => ({ ...s, [k]: v }));
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [o, d] = await Promise.all([
        api.get("/admin/offers"),
        api.get("/admin/declinations"),
      ]);
      setOffers(o.data.offers || []);
      setDeclinations(d.data.declinations || []);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Clear status banners when toggling modes so messages don't leak across.
  useEffect(() => {
    setError("");
    setSuccess("");
  }, [mode]);

  const parsePayRate = (raw) => {
    // Be forgiving: strip $, commas, spaces, slashes, and any trailing
    // unit (e.g. "/hr") before parsing. Returns null when nothing
    // numeric remains.
    if (raw === null || raw === undefined) return null;
    const cleaned = String(raw)
      .replace(/[$,\s]/g, "")
      .replace(/\/hr.*$/i, "")
      .trim();
    if (!cleaned) return null;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  const validateOffer = () => {
    if (!offerForm.candidate_name.trim()) return "Candidate name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(offerForm.candidate_email)) return "Valid candidate email is required.";
    if (!JOB_TITLES.includes(offerForm.job_title)) return "Please pick a job title from the dropdown.";
    if (!String(offerForm.pay_rate || "").trim()) return "Pay rate is required (e.g. 18.50).";
    const rate = parsePayRate(offerForm.pay_rate);
    if (rate === null || rate <= 0) return "Pay rate must be a positive number (e.g. 18.50).";
    if (!offerForm.start_date) return "Start date is required.";
    if (!MANAGERS.includes(offerForm.reporting_manager)) return "Reporting manager is required.";
    return "";
  };

  const validateDecl = () => {
    if (!declForm.candidate_name.trim()) return "Candidate name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(declForm.candidate_email)) return "Valid candidate email is required.";
    if (!declForm.position.trim()) return "Position interviewed for is required.";
    if (!declForm.interview_date) return "Interview date is required.";
    return "";
  };

  const sendOffer = async () => {
    setError(""); setSuccess("");
    const v = validateOffer();
    if (v) { setError(v); return; }
    setSending(true);
    try {
      const payRate = parsePayRate(offerForm.pay_rate);
      const payload = {
        ...offerForm,
        pay_rate: payRate,
        start_time: offerForm.start_time?.trim() || null,
        responsibilities: offerForm.responsibilities?.trim() || null,
      };
      const { data: createRes } = await api.post("/admin/offers", payload);
      await api.post(`/admin/offers/${createRes.offer.id}/send`);
      setSuccess(`Offer letter sent to ${createRes.offer.candidate_email}`);
      setOfferForm(emptyOfferForm());
      await loadAll();
    } catch (e) {
      const raw = formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Could not send offer letter.";
      // Friendlier message for the pay_rate <= 1000 backend cap (Pydantic emits raw text).
      const friendly = /less than or equal to 1000/i.test(raw)
        ? "Pay rate must be between $0.01 and $1000.00/hr."
        : /greater than 0/i.test(raw)
        ? "Pay rate must be a positive number (e.g. 18.50)."
        : raw;
      setError(friendly);
    } finally { setSending(false); }
  };

  const sendDecl = async () => {
    setError(""); setSuccess("");
    const v = validateDecl();
    if (v) { setError(v); return; }
    setSending(true);
    try {
      const { data: createRes } = await api.post("/admin/declinations", declForm);
      await api.post(`/admin/declinations/${createRes.declination.id}/send`);
      setSuccess(`Declination letter sent to ${createRes.declination.candidate_email}`);
      setDeclForm(emptyDeclForm());
      await loadAll();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Could not send declination letter.");
    } finally { setSending(false); }
  };

  const removeOffer = async (id) => {
    if (!window.confirm("Delete this offer record? This won't recall any email already sent.")) return;
    try { await api.delete(`/admin/offers/${id}`); await loadAll(); }
    catch (e) { setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };
  const removeDecl = async (id) => {
    if (!window.confirm("Delete this declination record? This won't recall any email already sent.")) return;
    try { await api.delete(`/admin/declinations/${id}`); await loadAll(); }
    catch (e) { setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  // Open a read-only viewer showing the EXACT HTML email that was (or would be) sent
  // for this saved record. We hit the dedicated GET endpoint so the modal reflects
  // server-side template changes without depending on locally cached form state.
  const openViewer = async (kind, item) => {
    setViewing({ kind, item, html: "", loading: true, error: "" });
    try {
      const endpoint =
        kind === "offer"
          ? `/admin/offers/${item.id}/html`
          : `/admin/declinations/${item.id}/html`;
      const { data } = await api.get(endpoint);
      setViewing({ kind, item, html: data.html || "", loading: false, error: "" });
    } catch (e) {
      setViewing({
        kind,
        item,
        html: "",
        loading: false,
        error: formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Could not load letter.",
      });
    }
  };
  const closeViewer = () => setViewing(null);

  const offerPreviewReady = !validateOffer();
  const declPreviewReady = !validateDecl();

  const isOfferMode = mode === MODE_OFFER;

  return (
    <div data-testid="hiring-tab" className="space-y-6">
      {/* Mode toggle pill */}
      <ModeToggle mode={mode} setMode={setMode} />

      <div className="grid lg:grid-cols-5 gap-6">
        {/* LEFT: Form + history */}
        <div className="lg:col-span-2 space-y-4">
          {isOfferMode ? (
            <OfferFormCard
              form={offerForm}
              update={updateOffer}
              sending={sending}
              error={error}
              success={success}
              onSend={sendOffer}
            />
          ) : (
            <DeclinationFormCard
              form={declForm}
              update={updateDecl}
              sending={sending}
              error={error}
              success={success}
              onSend={sendDecl}
            />
          )}

          <HistoryCard
            mode={mode}
            loading={loading}
            offers={offers}
            declinations={declinations}
            onDeleteOffer={removeOffer}
            onDeleteDecl={removeDecl}
            onOpenViewer={openViewer}
          />
        </div>

        {/* RIGHT: Live preview */}
        <div className="lg:col-span-3">
          <div className="rounded-2xl bg-brand-charcoal p-1 shadow-lg sticky top-4">
            <div className="rounded-[14px] bg-[#f5f4ef] overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 bg-brand-charcoal text-white">
                <div className="flex items-center gap-2">
                  <Eye className="h-3.5 w-3.5 text-brand-gold" />
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-brand-gold">Live Preview</p>
                </div>
                <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/65">
                  {(isOfferMode ? offerPreviewReady : declPreviewReady) ? "Ready to send" : "Fill the form to preview"}
                </p>
              </div>
              {isOfferMode ? (
                <OfferPreview form={offerForm} />
              ) : (
                <DeclinationPreview form={declForm} />
              )}
            </div>
          </div>
        </div>
      </div>

      {viewing && <LetterViewerModal viewing={viewing} onClose={closeViewer} />}
    </div>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const ModeToggle = ({ mode, setMode }) => (
  <div data-testid="hiring-mode-toggle" className="inline-flex rounded-full bg-white border border-black/5 p-1 shadow-sm">
    <button
      type="button"
      data-testid="hiring-mode-offer"
      onClick={() => setMode(MODE_OFFER)}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
        mode === MODE_OFFER
          ? "bg-brand-charcoal text-white"
          : "text-brand-charcoal/55 hover:text-brand-charcoal"
      }`}
    >
      <Briefcase className="h-3.5 w-3.5" />
      Offer Letter
    </button>
    <button
      type="button"
      data-testid="hiring-mode-declination"
      onClick={() => setMode(MODE_DECLINATION)}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
        mode === MODE_DECLINATION
          ? "bg-brand-charcoal text-white"
          : "text-brand-charcoal/55 hover:text-brand-charcoal"
      }`}
    >
      <UserX className="h-3.5 w-3.5" />
      Declination Letter
    </button>
  </div>
);

const FieldShell = ({ label, children }) => (
  <div>
    <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const SendBanner = ({ error, success }) => (
  <>
    {error && (
      <p data-testid="hiring-error" role="alert" className="mt-4 inline-flex items-start gap-2 text-[12px] text-red-600 leading-snug">
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        {error}
      </p>
    )}
    {success && (
      <p data-testid="hiring-success" className="mt-4 inline-flex items-start gap-2 text-[12px] text-emerald-700 leading-snug">
        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        {success}
      </p>
    )}
  </>
);

const OfferFormCard = ({ form, update, sending, error, success, onSend }) => (
  <div className="rounded-2xl bg-white border border-black/5 p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-1">
      <Briefcase className="h-4 w-4 text-brand-teal" />
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-teal">New Offer Letter</p>
    </div>
    <h2 className="font-display font-black text-[20px] text-brand-charcoal tracking-tight">Hire someone new.</h2>
    <p className="mt-1 text-[12.5px] text-brand-charcoal/65 leading-snug">
      Fill out the candidate details — the live preview on the right shows exactly what they&apos;ll receive.
    </p>

    <div className="mt-5 space-y-4">
      <FieldShell label="Candidate Name *">
        <Input data-testid="hiring-input-name" value={form.candidate_name} onChange={update("candidate_name")} placeholder="Jordan Smith" className="h-10 rounded-xl bg-brand-cream/40 border-black/10" />
      </FieldShell>
      <FieldShell label="Email Address *">
        <Input data-testid="hiring-input-email" type="email" value={form.candidate_email} onChange={update("candidate_email")} placeholder="jordan@example.com" className="h-10 rounded-xl bg-brand-cream/40 border-black/10" />
      </FieldShell>
      <FieldShell label="Job Title *">
        <select data-testid="hiring-input-title" value={form.job_title} onChange={update("job_title")} className="h-10 w-full rounded-xl bg-brand-cream/40 border border-black/10 px-3 text-[13px]">
          <option value="">Select job title…</option>
          {JOB_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </FieldShell>
      <div className="grid grid-cols-2 gap-3">
        <FieldShell label="Pay Rate ($/hr) *">
          <Input data-testid="hiring-input-rate" type="text" inputMode="decimal" value={form.pay_rate} onChange={update("pay_rate")} placeholder="18.50" className="h-10 rounded-xl bg-brand-cream/40 border-black/10" />
        </FieldShell>
        <FieldShell label="Start Date *">
          <Input data-testid="hiring-input-start" type="date" value={form.start_date} onChange={update("start_date")} className="h-10 rounded-xl bg-brand-cream/40 border-black/10" />
        </FieldShell>
      </div>
      <FieldShell label="Start Time (optional)">
        <select
          data-testid="hiring-input-start-time"
          value={form.start_time}
          onChange={update("start_time")}
          className="h-10 w-full rounded-xl bg-brand-cream/40 border border-black/10 px-3 text-[13px]"
        >
          <option value="">No specific time…</option>
          {START_TIME_SLOTS.map((slot) => (
            <option key={slot} value={slot}>{fmtTime(slot)}</option>
          ))}
        </select>
      </FieldShell>
      <FieldShell label="Reporting Manager *">
        <select data-testid="hiring-input-manager" value={form.reporting_manager} onChange={update("reporting_manager")} className="h-10 w-full rounded-xl bg-brand-cream/40 border border-black/10 px-3 text-[13px]">
          <option value="">Select manager…</option>
          {MANAGERS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </FieldShell>
      <div>
        <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
          Responsibilities (2-3 duties from SOP)
        </Label>
        <Textarea data-testid="hiring-input-responsibilities" value={form.responsibilities} onChange={update("responsibilities")} placeholder="e.g. greeting guests, taking orders accurately, and maintaining station cleanliness per Operations Manual." rows={3} className="mt-1.5 rounded-xl bg-brand-cream/40 border-black/10 text-[13px] leading-relaxed" />
        <p className="mt-1 text-[10.5px] text-brand-charcoal/55">
          Leave blank to render a placeholder the candidate can see.
        </p>
      </div>
    </div>

    <SendBanner error={error} success={success} />

    <button
      type="button"
      data-testid="hiring-send-btn"
      onClick={onSend}
      disabled={sending}
      className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      {sending ? "Sending…" : "Send Offer Letter"}
    </button>
    <p className="mt-2 text-[10.5px] text-brand-charcoal/55 text-center">
      CC&apos;d to <span className="font-bold text-brand-charcoal/75">info@btwnyc.com</span> for your records.
    </p>
  </div>
);

const DeclinationFormCard = ({ form, update, sending, error, success, onSend }) => (
  <div className="rounded-2xl bg-white border border-black/5 p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-1">
      <UserX className="h-4 w-4 text-brand-teal" />
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-teal">New Declination Letter</p>
    </div>
    <h2 className="font-display font-black text-[20px] text-brand-charcoal tracking-tight">Close the loop respectfully.</h2>
    <p className="mt-1 text-[12.5px] text-brand-charcoal/65 leading-snug">
      A warm, professional send-off after an interview — your live preview is on the right.
    </p>

    <div className="mt-5 space-y-4">
      <FieldShell label="Candidate Name *">
        <Input data-testid="decl-input-name" value={form.candidate_name} onChange={update("candidate_name")} placeholder="Jordan Smith" className="h-10 rounded-xl bg-brand-cream/40 border-black/10" />
      </FieldShell>
      <FieldShell label="Email Address *">
        <Input data-testid="decl-input-email" type="email" value={form.candidate_email} onChange={update("candidate_email")} placeholder="jordan@example.com" className="h-10 rounded-xl bg-brand-cream/40 border-black/10" />
      </FieldShell>
      <FieldShell label="Position Interviewed For *">
        <select data-testid="decl-input-position" value={form.position} onChange={update("position")} className="h-10 w-full rounded-xl bg-brand-cream/40 border border-black/10 px-3 text-[13px]">
          <option value="">Select position…</option>
          {JOB_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </FieldShell>
      <FieldShell label="Interview Date *">
        <Input data-testid="decl-input-interview-date" type="date" value={form.interview_date} onChange={update("interview_date")} className="h-10 rounded-xl bg-brand-cream/40 border-black/10" />
      </FieldShell>
    </div>

    <SendBanner error={error} success={success} />

    <button
      type="button"
      data-testid="decl-send-btn"
      onClick={onSend}
      disabled={sending}
      className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      {sending ? "Sending…" : "Send Declination Letter"}
    </button>
    <p className="mt-2 text-[10.5px] text-brand-charcoal/55 text-center">
      CC&apos;d to <span className="font-bold text-brand-charcoal/75">info@btwnyc.com</span> for your records.
    </p>
  </div>
);

const HistoryCard = ({ mode, loading, offers, declinations, onDeleteOffer, onDeleteDecl, onOpenViewer }) => {
  const isOffer = mode === MODE_OFFER;
  const items = isOffer ? offers : declinations;
  const label = isOffer ? "Sent offers" : "Sent declinations";
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-teal">History</p>
          <h3 className="font-display font-black text-[15px] text-brand-charcoal tracking-tight mt-0.5">
            {label}
          </h3>
          <p className="mt-1 text-[10.5px] text-brand-charcoal/55">
            Tap any letter to see exactly what was sent.
          </p>
        </div>
        <span data-testid="hiring-history-count" className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
          {items.length} total
        </span>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-[12.5px] text-brand-charcoal/55">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p data-testid="hiring-history-empty" className="text-[12.5px] text-brand-charcoal/55">
          {isOffer ? "No offers yet. Use the form above to send your first." : "No declinations yet. Use the form above to send your first."}
        </p>
      ) : (
        <ul data-testid="hiring-history-list" className="space-y-2">
          {items.map((o) => (
            isOffer ? (
              <OfferHistoryRow key={o.id} offer={o} onDelete={onDeleteOffer} onOpen={() => onOpenViewer("offer", o)} />
            ) : (
              <DeclinationHistoryRow key={o.id} decl={o} onDelete={onDeleteDecl} onOpen={() => onOpenViewer("declination", o)} />
            )
          ))}
        </ul>
      )}
    </div>
  );
};

const HistoryStatusBadge = ({ status, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.1em] ${
      status === "sent"
        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
        : "bg-amber-50 text-amber-700 border border-amber-200"
    }`}
  >
    {status === "sent" ? <CheckCircle2 className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
    {status}
  </span>
);

const OfferHistoryRow = ({ offer, onDelete, onOpen }) => (
  <li data-testid={`hiring-history-${offer.id}`} className="rounded-xl border border-black/5 bg-brand-cream/30 p-3 hover:bg-brand-cream/60 transition-colors">
    <div className="flex items-start justify-between gap-2">
      <button
        type="button"
        data-testid={`hiring-view-${offer.id}`}
        onClick={onOpen}
        className="min-w-0 flex-1 text-left bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-teal/50 rounded-md"
        aria-label={`View offer letter sent to ${offer.candidate_name}`}
      >
        <p className="font-bold text-brand-charcoal text-[13.5px] leading-tight truncate">{offer.candidate_name}</p>
        <p className="text-[11.5px] text-brand-charcoal/65 truncate">{offer.candidate_email}</p>
        <p className="text-[11.5px] text-brand-charcoal/65 mt-0.5">
          {offer.job_title} · {fmtMoney(offer.pay_rate)} · Starts {fmtDate(offer.start_date)}
        </p>
        <p className="text-[10.5px] text-brand-charcoal/50 mt-1">
          Reports to {offer.reporting_manager} · Sent {fmtSent(offer.sent_at)}
        </p>
      </button>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <HistoryStatusBadge status={offer.status} testid={`hiring-status-${offer.id}`} />
        <button type="button" data-testid={`hiring-delete-${offer.id}`} onClick={() => onDelete(offer.id)} className="text-brand-charcoal/40 hover:text-red-600 transition-colors" aria-label="Delete offer">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  </li>
);

const DeclinationHistoryRow = ({ decl, onDelete, onOpen }) => (
  <li data-testid={`decl-history-${decl.id}`} className="rounded-xl border border-black/5 bg-brand-cream/30 p-3 hover:bg-brand-cream/60 transition-colors">
    <div className="flex items-start justify-between gap-2">
      <button
        type="button"
        data-testid={`decl-view-${decl.id}`}
        onClick={onOpen}
        className="min-w-0 flex-1 text-left bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-teal/50 rounded-md"
        aria-label={`View declination letter sent to ${decl.candidate_name}`}
      >
        <p className="font-bold text-brand-charcoal text-[13.5px] leading-tight truncate">{decl.candidate_name}</p>
        <p className="text-[11.5px] text-brand-charcoal/65 truncate">{decl.candidate_email}</p>
        <p className="text-[11.5px] text-brand-charcoal/65 mt-0.5">
          {decl.position} · Interviewed {fmtDate(decl.interview_date)}
        </p>
        <p className="text-[10.5px] text-brand-charcoal/50 mt-1">Sent {fmtSent(decl.sent_at)}</p>
      </button>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <HistoryStatusBadge status={decl.status} testid={`decl-status-${decl.id}`} />
        <button type="button" data-testid={`decl-delete-${decl.id}`} onClick={() => onDelete(decl.id)} className="text-brand-charcoal/40 hover:text-red-600 transition-colors" aria-label="Delete declination">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  </li>
);

// ----------------------------------------------------------------------------
// Live preview panes
// ----------------------------------------------------------------------------

const PreviewHeader = ({ subhead }) => (
  <>
    <div className="flex items-center justify-center gap-6 pb-4">
      <img src={BTW_LOGO} alt="#BTW" data-testid="hiring-preview-btw-logo" className="h-16 w-auto" />
      <img src={F3_LOGO} alt="Fresh Fast Foods" data-testid="hiring-preview-f3-logo" className="h-16 w-auto" />
    </div>
    <div className="text-center pb-5 border-b-2 border-brand-charcoal">
      <p className="text-[12px] font-black uppercase tracking-[0.22em] text-brand-charcoal">
        Burgers Tacos Wings &amp; Seafood (BTW)
      </p>
      {subhead && (
        <p className="mt-1.5 text-[10.5px] font-bold uppercase tracking-[0.22em] text-brand-teal">
          {subhead}
        </p>
      )}
    </div>
  </>
);

const OwnerSignature = () => (
  <div className="mt-7">
    <p className="font-display font-black italic text-brand-charcoal text-[18px] leading-none">
      Philip &quot;Power&quot; Armstrong
    </p>
    <p className="mt-1 text-[10.5px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60">
      Owner, BTW NYC
    </p>
  </div>
);

const OfferPreview = ({ form }) => {
  const safeName = form.candidate_name || "[Candidate Name]";
  const safeTitle = form.job_title || "[Job Title]";
  const startDatePretty = form.start_date ? fmtDate(form.start_date) : "";
  const startTimePretty = form.start_time ? fmtTime(form.start_time) : "";
  const safeStartCell = startDatePretty
    ? (
        <>
          {startDatePretty}
          {startTimePretty && (
            <span className="block mt-0.5 font-semibold text-brand-charcoal/70 text-[12.5px]">
              {startTimePretty}
            </span>
          )}
        </>
      )
    : "[Start Date]";
  const safeManager = friendlyManager(form.reporting_manager) || "[Ms. Maddie/Mr. Power/Shift Manager]";
  return (
    <div data-testid="hiring-preview" className="p-7 bg-white">
      <PreviewHeader subhead="Employee Offer Letter" />
      <div className="mt-6 text-[13.5px] text-brand-charcoal/85 leading-relaxed">
        <p>Dear {safeName},</p>
        <p className="mt-3">
          We are pleased to offer you the position of{" "}
          <strong className="text-brand-charcoal">{safeTitle}</strong> at
          Burgers Tacos Wings &amp; Seafood (BTW) in Queens, NY.
        </p>
        <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-teal">Key Details</p>
        <div className="mt-2 rounded-xl border border-black/5 bg-brand-cream/40 overflow-hidden">
          <KeyDetailRow label="Start Date" value={safeStartCell} />
          <KeyDetailRow label="Pay Rate" value={`${form.pay_rate ? `$${Number(form.pay_rate).toFixed(2)}` : "—"} per hour`} />
          <KeyDetailRow label="Reporting To" value={safeManager} last />
        </div>
        <p className="mt-5">
          At BTW, we believe that{" "}
          <strong className="text-brand-charcoal">quality food starts with quality people</strong>.
          You are joining a team that is not only dedicated to serving the best wings and tacos
          in Queens but also to building a stronger community through our leadership and
          workforce programs.
        </p>
        <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-teal">Responsibilities</p>
        <p className="mt-2">
          Your role will involve{" "}
          <span className={form.responsibilities ? "" : "italic text-brand-charcoal/55"}>
            {form.responsibilities || "[Briefly list 2-3 duties from the SOP Manual]"}
          </span>
          . You will be expected to follow the BTW standards for quality, cleanliness, and
          hospitality as outlined in our Operations Manual.
        </p>
        <p className="mt-5">
          <strong className="text-brand-charcoal">We look forward to building the future of BTW with you!</strong>
        </p>
        <div
          data-testid="hiring-preview-accept-cta"
          className="mt-6 rounded-xl border border-black/10 bg-brand-cream/60 p-5 text-center"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-teal">
            How to Accept
          </p>
          <h3 className="mt-1.5 font-display font-black text-brand-charcoal text-[16px] tracking-tight">
            Tap below to formally accept your offer
          </h3>
          <a
            href={`mailto:info@btwnyc.com?subject=${encodeURIComponent("I accept the BTW offer — " + (form.job_title || ""))}&body=${encodeURIComponent("Hi BTW team,\n\nI, " + (form.candidate_name || "[Candidate]") + ", accept the offer for the " + (form.job_title || "[Position]") + " position with a start date of " + (form.start_date ? fmtDate(form.start_date) : "[Start Date]") + ".\n\nThank you!\n")}`}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[11px] font-black uppercase tracking-[0.14em] no-underline hover:bg-black/85 transition-colors"
          >
            I Accept the Offer →
          </a>
          <p className="mt-3 text-[11px] text-brand-charcoal/55 leading-relaxed">
            Opens your email app with a pre-filled acceptance — just hit send.
            You can also text us at (929) 272-5704.
          </p>
        </div>
        <OwnerSignature />
        <div
          data-testid="hiring-preview-homebase"
          className="mt-7 rounded-xl border border-dashed border-brand-gold bg-brand-cream/60 p-5 text-center"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-teal">
            Next Step · After You Sign
          </p>
          <h3 className="mt-1.5 font-display font-black text-brand-charcoal text-[16px] tracking-tight">
            Complete your onboarding on Homebase
          </h3>
          <p className="mt-2 text-[11.5px] text-brand-charcoal/70 leading-relaxed">
            Your manager will text you your personal Homebase onboarding link within 24 hours.
            That&apos;s where you&apos;ll complete paperwork, tax forms, and direct deposit setup.
          </p>
        </div>

        <div
          data-testid="hiring-preview-day-one"
          className="mt-5 rounded-xl border border-black/10 bg-white p-5"
        >
          <p className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-brand-teal">
            What to Bring on Day 1
          </p>
          <h3 className="mt-1.5 text-center font-display font-black text-brand-charcoal text-[16px] tracking-tight">
            Quick checklist for your first shift
          </h3>
          <div
            data-testid="hiring-preview-day-one-duration"
            className="mt-3 rounded-lg border border-dashed border-brand-gold bg-brand-cream/60 px-4 py-3 text-center"
          >
            <p className="text-[12.5px] text-brand-charcoal font-bold leading-snug">
              Plan to be here for <span className="text-brand-charcoal">3–4 hours</span> on your first day.
            </p>
            <p className="mt-1 text-[11.5px] text-brand-charcoal/65 leading-snug">
              Don&apos;t worry about food — <strong className="text-brand-charcoal">lunch will be provided</strong>.
            </p>
          </div>
          <ul className="mt-4 divide-y divide-black/5 border-y border-black/5">
            {[
              { n: 1, label: "Government-issued Photo ID", helper: "driver's license, state ID, or passport" },
              { n: 2, label: "Social Security card", helper: "needed for I-9 + tax paperwork" },
              { n: 3, label: "Any relevant certifications", helper: "Food Handler, ServSafe, etc. — bring the originals or printed copies." },
            ].map((item) => (
              <li key={item.n} className="flex items-start gap-3 py-2.5">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-charcoal text-white text-[10px] font-bold">
                  {item.n}
                </span>
                <p className="text-[12.5px] text-brand-charcoal leading-snug">
                  <strong className="text-brand-charcoal">{item.label}</strong>
                  <span className="text-brand-charcoal/55"> ({item.helper})</span>
                </p>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-center text-[11px] text-brand-charcoal/55 leading-relaxed">
            Missing something? Text us at (929) 272-5704 <em>before</em> your start date so we can sort it out.
          </p>
        </div>

        <div
          data-testid="hiring-preview-dress-code"
          className="mt-5 rounded-xl border border-black/10 bg-brand-cream/50 p-5"
        >
          <p className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-brand-teal">
            First Day · Dress Code
          </p>
          <h3 className="mt-1.5 text-center font-display font-black text-brand-charcoal text-[16px] tracking-tight">
            What to Wear on Your First Day
          </h3>
          <p className="mt-2 text-center text-[12.5px] text-brand-charcoal/75">
            Please arrive dressed and ready for work.
          </p>

          <div className="mt-3 rounded-lg bg-white border border-black/5 p-3.5">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-brand-charcoal">Bring</p>
            <ul className="mt-1 divide-y divide-black/5">
              {[
                { key: "pants", label: <>Clean <strong>black pants</strong></>, helper: "" },
                { key: "shoes", label: <>Clean, <strong>closed-toe, non-slip shoes</strong></>, helper: "your choice of style" },
                { key: "attitude", label: <>A <strong>positive attitude and willingness to learn!</strong></>, helper: "" },
              ].map((it) => (
                <li key={it.key} className="flex items-start gap-2 py-1.5">
                  <span className="text-brand-teal font-black text-[14px] leading-tight w-4 shrink-0 text-center">✓</span>
                  <p className="text-[12.5px] text-brand-charcoal leading-snug">
                    {it.label}
                    {it.helper && <span className="text-brand-charcoal/55"> ({it.helper})</span>}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-3 rounded-lg bg-white border border-black/5 p-3.5">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-brand-charcoal">BTW Will Provide</p>
            <ul className="mt-1 divide-y divide-black/5">
              {[
                "Your role-specific uniform shirt",
                "A clean apron for each shift",
                "A hat (if requested)",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 py-1.5">
                  <span className="text-brand-gold font-black text-[14px] leading-tight w-4 shrink-0 text-center">★</span>
                  <p className="text-[12.5px] text-brand-charcoal leading-snug">{item}</p>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-3 text-center text-[11.5px] text-brand-charcoal/55 leading-relaxed">
            Please arrive well-groomed, practice good personal hygiene, and be ready to represent the
            BTW brand with professionalism and pride.
          </p>
        </div>

        <a
          href={HYGIENE_DOC_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hiring-preview-hygiene"
          className="mt-5 block rounded-xl bg-brand-charcoal text-white p-6 text-center no-underline hover:bg-black/85 transition-colors"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand-teal">
            Required Reading
          </p>
          <h3 className="mt-1.5 font-display font-black text-[18px] tracking-tight text-white">
            BTW Employee Hygiene Requirements
          </h3>
          <p className="mt-1.5 text-[11.5px] text-white/65 leading-relaxed">
            Clean habits · Safe food · Respect every time.<br />
            Please review and follow these standards from your very first shift.
          </p>
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-brand-charcoal px-5 py-2 text-[10.5px] font-black uppercase tracking-[0.16em]">
            View &amp; Download Hygiene Standards →
          </span>
          <p className="mt-3 text-[10px] text-white/50 leading-snug">
            Opens the full BTW hygiene infographic in your browser — save or print it for reference.
          </p>
        </a>
      </div>
    </div>
  );
};

const DeclinationPreview = ({ form }) => {
  const safeName = form.candidate_name || "[Candidate Name]";
  const safePosition = form.position || "[Position]";
  const safeInterview = form.interview_date ? fmtDate(form.interview_date) : "[Interview Date]";
  return (
    <div data-testid="decl-preview" className="p-7 bg-white">
      <PreviewHeader subhead="Candidate Update" />
      <div className="mt-6 text-[13.5px] text-brand-charcoal/85 leading-relaxed">
        <p>Dear {safeName},</p>
        <p className="mt-3">
          Thank you for taking the time to interview with us for the{" "}
          <strong className="text-brand-charcoal">{safePosition}</strong> role on{" "}
          <strong className="text-brand-charcoal">{safeInterview}</strong>.
          We genuinely enjoyed meeting you and appreciated the energy and care
          you brought to the conversation.
        </p>
        <p className="mt-4">
          This was a tough decision. After careful consideration, we&apos;ve
          decided to move forward with another candidate whose background more
          closely aligned with what we need for this particular role at this time.
        </p>
        <p className="mt-4">
          We want you to know that this outcome takes nothing away from your
          talent or the strong impression you made on our team. We wish you
          nothing but success in everything that comes next.
        </p>
        <p className="mt-6">With gratitude,</p>
        <OwnerSignature />
      </div>
    </div>
  );
};

const KeyDetailRow = ({ label, value, last = false }) => (
  <div className={`flex items-start gap-4 px-4 py-3 ${last ? "" : "border-b border-black/5"}`}>
    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 w-28 shrink-0 mt-0.5">
      {label}
    </p>
    <div className="font-bold text-brand-charcoal text-[13.5px] leading-tight flex-1 min-w-0">{value}</div>
  </div>
);

// ----------------------------------------------------------------------------
// Letter viewer modal — renders the exact HTML email that was (or would be)
// sent for a saved record. We inject the HTML into a sandboxed iframe so the
// email's inline styles don't bleed into the admin app.
// ----------------------------------------------------------------------------
const LetterViewerModal = ({ viewing, onClose }) => {
  const { kind, item, html, loading, error } = viewing;
  const isOffer = kind === "offer";
  const headline = isOffer ? "Offer Letter Sent" : "Declination Letter Sent";
  const subline = isOffer
    ? `${item.candidate_name} · ${item.job_title || ""}`
    : `${item.candidate_name} · ${item.position || ""}`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      data-testid="hiring-letter-viewer"
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[92vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-black/5 bg-brand-charcoal text-white">
          <div className="min-w-0">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-brand-gold">
              {headline}
            </p>
            <h3 className="mt-0.5 font-display font-black text-[16px] tracking-tight truncate">
              {subline}
            </h3>
            <p className="mt-0.5 text-[11px] text-white/65 truncate">
              To {item.candidate_email} · Sent {fmtSent(item.sent_at)}
            </p>
          </div>
          <button
            type="button"
            data-testid="hiring-letter-viewer-close"
            onClick={onClose}
            className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Close letter viewer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden bg-[#f5f4ef]">
          {loading ? (
            <div className="h-full flex items-center justify-center gap-2 text-[12.5px] text-brand-charcoal/65">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading the letter you sent…
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center p-6">
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12.5px] text-red-700">
                {error}
              </div>
            </div>
          ) : (
            <iframe
              data-testid="hiring-letter-viewer-frame"
              title={headline}
              srcDoc={html}
              sandbox=""
              className="w-full h-full min-h-[60vh] bg-white"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HiringTab;
